import {
  Box,
  ContentBlock,
  Heading,
  Stack,
  TextLink,
  Text,
} from 'braid-design-system';
import { useEffect } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import {
  EVENT_NAME_ALERT_VIEWED,
  EVENT_NAME_ML_CTA_PRESSED,
} from 'src/app/shared/dataAnalytics/schema/constants';
import { getProductFeatureByType } from 'src/app/utils/getProductFeatureByType';
import getTrackingAdapter from 'src/app/v1/dataAnalytics';
import { useLocalisedRoutes } from 'src/app/v1/views/hooks/useLocalisedRoutes';
import PageLayout from 'src/app/v1/views/layouts/PageLayout';
import type { AppState } from 'src/types/appState';
import type { TrackingEvent } from 'src/types/trackingData';

import { ErrorSvg } from './ErrorSvg';

interface IMultiLocationUnavailable {
  jobId?: string;
}

export const MultiLocationUnavailableSecondaryText: React.FC<{
  copyJobUrl: string;
  onClickCopyJobUrl: React.MouseEventHandler<HTMLAnchorElement>;
}> = ({ copyJobUrl, onClickCopyJobUrl }) => {
  const productFeatures = useSelector(
    (state: AppState) => state.session.productFeatures,
    shallowEqual,
  );
  const isPerUnitBilling = Boolean(
    getProductFeatureByType({
      featureType: 'PerUnitBilling',
      productFeatures,
    }),
  );

  return (
    <Text tone="secondary" align="center">
      {!isPerUnitBilling && (
        <>
          Right now, you can only add locations in some regions. <br />
        </>
      )}
      <TextLink href={copyJobUrl} onClick={onClickCopyJobUrl}>
        Copy your ad
      </TextLink>{' '}
      to post it to more locations.
    </Text>
  );
};

export const MultiLocationUnavailable = ({
  jobId,
}: IMultiLocationUnavailable) => {
  const { getCopyJobRoute } = useLocalisedRoutes();

  const navigate = useNavigate();

  useEffect(() => {
    const eventData: TrackingEvent = {
      eventName: EVENT_NAME_ALERT_VIEWED,
      alertShown: [
        {
          tone: 'critical',
          position: 'content',
          message: 'Add location isn’t available',
          messageCode: 'Multi location not available',
        },
      ],
    };

    getTrackingAdapter.sendLinkEvent(eventData);
  }, []);

  const onClickCopyJobUrl: React.MouseEventHandler<HTMLAnchorElement> = (
    event,
  ) => {
    event.preventDefault();
    const eventData: TrackingEvent = {
      eventName: EVENT_NAME_ML_CTA_PRESSED,
      actionOrigin: 'multi location not available',
      callToAction: 'copy ad',
    };

    getTrackingAdapter.sendLinkEvent(eventData);
    navigate(getCopyJobRoute(jobId!));
  };

  return (
    <PageLayout hasProgressBar={false}>
      <ContentBlock width="small">
        <Box paddingY="xxlarge">
          <Box background="surface" paddingY="xlarge" paddingX="gutter">
            <Stack space="medium" align="center">
              <Box
                style={{
                  width: '128px',
                  height: '128px',
                }}
              >
                <ErrorSvg />
              </Box>
              <Stack space="gutter" align="center">
                <Heading level="3">Add locations isn&apos;t available</Heading>
                {Boolean(jobId) && (
                  <MultiLocationUnavailableSecondaryText
                    copyJobUrl={getCopyJobRoute(jobId!)}
                    onClickCopyJobUrl={onClickCopyJobUrl}
                  />
                )}
              </Stack>
            </Stack>
          </Box>
        </Box>
      </ContentBlock>
    </PageLayout>
  );
};
